import type { CartItemEvent } from "./bridge";

/**
 * Cart changes as both sides announce them (ADR-6): the server's webhooks and the hosted
 * page's bridge events classify and shape a change here, so the two never disagree.
 */

/** What one basket call takes (public and v1 routes). A bigger host snapshot goes in chunks. */
export const BASKET_LIMITS = { items: 50, quantity: 99 } as const;

/** One line's change in a basket call, in the retailer's ids, before and after. */
export interface CartChange {
	/** The product's catalog id. */
	externalId: string;
	/** The product's id in the basket's store: the cart key. */
	itemId: string;
	title: string;
	/** The product's main image, an absolute URL. */
	imageUrl?: string | null;
	quantity: number;
	previousQuantity: number;
	/** Decimal string in the store currency. */
	unitPrice: string;
	substitution?: CartItemEvent["item"]["substitution"];
	/** With SPECIFIC: the item to pack instead, by its id in the same store. */
	substituteItemId?: string;
}

export type CartItemEventType =
	| "cart.item_added"
	| "cart.item_updated"
	| "cart.item_removed";

export function cartEventType(
	change: Pick<CartChange, "quantity" | "previousQuantity">,
): CartItemEventType {
	return change.previousQuantity === 0
		? "cart.item_added"
		: change.quantity === 0
			? "cart.item_removed"
			: "cart.item_updated";
}

export function cartEventItem(
	change: CartChange,
	currency: string,
): CartItemEvent["item"] {
	return {
		itemId: change.itemId,
		catalogId: change.externalId,
		quantity: change.quantity,
		previousQuantity: change.previousQuantity,
		unitPrice: change.unitPrice,
		currency,
		title: change.title,
		...(change.imageUrl ? { imageUrl: change.imageUrl } : {}),
		...(change.substitution ? { substitution: change.substitution } : {}),
		...(change.substituteItemId
			? { substituteItemId: change.substituteItemId }
			: {}),
	};
}
